import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billing } from '@/api/billing';
import { useRequireOrg } from '@/tenancy/OrgContext';
import { useAuth } from '@/auth/AuthContext';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import EmptyState from '@/components/EmptyState';
import { CREDITS_QUERY_KEY } from '@/components/CreditsWidget';
import TablePageSize from '@/components/TablePageSize';
import { useTablePageSize } from '@/lib/useTablePageSize';
import { isPaddleConfigured, openPaddleCheckout } from '@/lib/paddle';
import { DEMO_READ_ONLY_MESSAGE, isDemoMode } from '@/demo/demoMode';
import { formatDateTime, formatMoney } from '@/lib/format';
import type {
  CreditProductView,
  CreditTransactionType,
} from '@/api/types';

const TRANSACTION_LABELS: Record<CreditTransactionType, string> = {
  INITIAL_GRANT: 'Initial grant',
  PURCHASE: 'Purchase',
  ANALYSIS_CHARGE: 'Analysis',
  REFUND: 'Refund',
  ADJUSTMENT: 'Adjustment',
};

export default function BillingPage() {
  const { id: orgId } = useRequireOrg();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useTablePageSize('billing-history', 20);
  const [checkoutStatus, setCheckoutStatus] = useState<
    null | { kind: 'polling' | 'success' | 'pending' | 'error'; message: string }
  >(null);

  const balanceQuery = useQuery({
    queryKey: [...CREDITS_QUERY_KEY, orgId],
    queryFn: () => billing.balance(orgId),
  });

  const productsQuery = useQuery({
    queryKey: ['billing', 'products'],
    queryFn: () => billing.products(),
  });

  const historyQuery = useQuery({
    queryKey: ['billing', 'history', orgId, page, pageSize],
    queryFn: () => billing.history(orgId, page, pageSize),
  });

  // Paddle redirects back here with ?status=success once the user completes checkout.
  // The webhook is async so the balance may not be updated yet — poll for up to 30s.
  useEffect(() => {
    if (searchParams.get('status') !== 'success') return;

    const initialBalance = balanceQuery.data?.balance ?? null;
    setCheckoutStatus({ kind: 'polling', message: 'Processing payment…' });

    let cancelled = false;
    const startedAt = Date.now();

    // Refresh history on every tick too — Paddle's pre-completion webhooks
    // (transaction.created/.paid) write a PENDING row that we want to surface
    // to the user as soon as it lands, even before credits are applied.
    const tick = async () => {
      if (cancelled) return;
      try {
        const fresh = await billing.balance(orgId);
        qc.setQueryData([...CREDITS_QUERY_KEY, orgId], fresh);
        qc.invalidateQueries({ queryKey: ['billing', 'history', orgId] });
        if (initialBalance == null || fresh.balance > initialBalance) {
          setCheckoutStatus({ kind: 'success', message: 'Credits applied!' });
          return;
        }
      } catch {
        // swallow — we'll retry on the next tick.
      }
      if (Date.now() - startedAt > 30_000) {
        setCheckoutStatus({
          kind: 'pending',
          message:
            'Still processing — the purchase is shown as pending in your history and credits will be applied once Paddle confirms the payment. Contact support if it does not clear within a few minutes.',
        });
        return;
      }
      setTimeout(tick, 2_000);
    };

    tick();

    // Clear ?status=success from the URL so a reload does not re-trigger polling.
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    setSearchParams(next, { replace: true });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('status'), orgId]);

  const handleBuy = async (product: CreditProductView) => {
    if (isDemoMode()) {
      setCheckoutStatus({ kind: 'error', message: DEMO_READ_ONLY_MESSAGE });
      return;
    }
    if (!isPaddleConfigured()) {
      setCheckoutStatus({
        kind: 'error',
        message:
          'Paddle is not configured. Set VITE_PADDLE_CLIENT_TOKEN before enabling checkout.',
      });
      return;
    }
    try {
      await openPaddleCheckout({
        paddlePriceId: product.paddlePriceId,
        organizationId: orgId,
        customerEmail: user?.email,
      });
    } catch (err) {
      setCheckoutStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to open checkout',
      });
    }
  };

  const balance = balanceQuery.data?.balance ?? null;

  return (
    <>
      <PageHeader
        title="Billing & credits"
        subtitle="Top up credits to keep analyzing pull requests."
      />

      {checkoutStatus ? (
        <div
          className={`billing-status billing-status-${checkoutStatus.kind}`}
          role="status"
        >
          {checkoutStatus.kind === 'polling' ? <Spinner /> : null}
          <span>{checkoutStatus.message}</span>
          <button
            className="btn btn-ghost"
            onClick={() => setCheckoutStatus(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ) : null}

      {/* ---------- Current balance ---------- */}
      <section className="card">
        <h3>Current balance</h3>
        {balanceQuery.isLoading ? (
          <Spinner label="Loading balance…" />
        ) : balanceQuery.error ? (
          <ErrorBanner error={balanceQuery.error} onRetry={() => balanceQuery.refetch()} />
        ) : (
          <div className="balance-block">
            <div className="balance-number">{balance ?? '—'}</div>
            <div className="muted">
              {balance === 0
                ? 'Out of credits — buy a pack below to keep going'
                : balance != null && balance < 10
                  ? 'Running low' : ''}
            </div>
          </div>
        )}
      </section>

      {/* ---------- Buy more ---------- */}
      <section className="card">
        <h3>Buy more credits</h3>
        {productsQuery.isLoading ? (
          <Spinner label="Loading products…" />
        ) : productsQuery.error ? (
          <ErrorBanner
            error={productsQuery.error}
            onRetry={() => productsQuery.refetch()}
          />
        ) : !productsQuery.data || productsQuery.data.length === 0 ? (
          <EmptyState
            title="No credit packs available"
            description="Check back later — products will appear here once configured."
          />
        ) : (
          <>
            <div className="product-grid">
              {[...productsQuery.data]
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map(product => (
                  <article key={product.id} className="product-card">
                    <h4>{product.name}</h4>
                    <div className="product-credits">
                      {product.credits}
                      <span className="product-credits-label"> credits</span>
                    </div>
                    <div className="product-price">
                      {formatMoney(product.priceCents, product.currency)}
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleBuy(product)}
                    >
                      Buy
                    </button>
                  </article>
                ))}
            </div>
            <p className="billing-legal muted small">
              By purchasing, you agree to our{' '}
              <a
                href="https://insightpm.one/terms-and-conditions/index.html"
                target="_blank"
                rel="noreferrer"
                className="link"
              >
                Terms &amp; Conditions
              </a>
              ,{' '}
              <a
                href="https://insightpm.one/privacy-policy/index.html"
                target="_blank"
                rel="noreferrer"
                className="link"
              >
                Privacy Policy
              </a>
              , and{' '}
              <a
                href="https://insightpm.one/refund-policy/index.html"
                target="_blank"
                rel="noreferrer"
                className="link"
              >
                Refund Policy
              </a>
              .
            </p>
          </>
        )}
      </section>

      {/* ---------- History ---------- */}
      <section className="card">
        <h3>History</h3>
        {historyQuery.isLoading ? (
          <Spinner label="Loading history…" />
        ) : historyQuery.error ? (
          <ErrorBanner
            error={historyQuery.error}
            onRetry={() => historyQuery.refetch()}
          />
        ) : !historyQuery.data || historyQuery.data.items.length === 0 ? (
          <EmptyState
            title="No credit activity yet"
            description="Initial grants, purchases, and analysis charges will appear here."
          />
        ) : (
          <>
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Change</th>
                  <th>Balance</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {historyQuery.data.items.map(tx => {
                  const isPending = tx.status === 'PENDING';
                  return (
                    <tr
                      key={tx.id}
                      className={isPending ? 'credit-row-pending' : undefined}
                    >
                      <td className="muted small">{formatDateTime(tx.createdAt)}</td>
                      <td>
                        {TRANSACTION_LABELS[tx.type] ?? tx.type}
                        {isPending ? (
                          <span className="badge badge-pending" title="Awaiting Paddle confirmation">
                            Pending
                          </span>
                        ) : null}
                      </td>
                      <td
                        className={
                          tx.delta > 0
                            ? 'credit-delta credit-delta-pos'
                            : 'credit-delta credit-delta-neg'
                        }
                      >
                        {isPending
                          ? `+${tx.delta} (pending)`
                          : tx.delta > 0
                            ? `+${tx.delta}`
                            : tx.delta}
                      </td>
                      <td>{isPending ? '—' : tx.balanceAfter}</td>
                      <td className="notes-cell">
                        {tx.analysisId ? (
                          <Link to={`/analyses/${tx.analysisId}`} className="link">
                            View analysis
                          </Link>
                        ) : isPending ? (
                          'Credits will appear once Paddle confirms the payment.'
                        ) : (
                          tx.note ?? '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            <div className="pagination">
              <button
                className="btn btn-ghost"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                ← Previous
              </button>
              <span className="muted">
                Page {historyQuery.data.page + 1} of {historyQuery.data.totalPages || 1}
              </span>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <TablePageSize
                  value={pageSize}
                  onChange={n => {
                    setPageSize(n);
                    setPage(0);
                  }}
                />
                <button
                  className="btn btn-ghost"
                  disabled={historyQuery.data.page + 1 >= (historyQuery.data.totalPages || 1)}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </>
  );
}
